import { makeAutoObservable, runInAction } from 'mobx';

class HighlightsStore {
  highlights = [];
  followingHighlights = [];
  loading = false;
  error = null;
  rootStore = null;

  constructor(rootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  // Set loading state
  setLoading(loading) {
    this.loading = loading;
  }

  // Set error state
  setError(error) {
    this.error = error;
  }

  // Fetch user's highlights
  async fetchHighlights() {
    this.setLoading(true);
    this.setError(null);

    try {
      const response = await this.rootStore.authenticatedApiCall('api/highlights');
      
      runInAction(() => {
        this.highlights = response;
        this.loading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error.message;
        this.loading = false;
      });
      throw error;
    }
  }

  // Fetch following highlights
  async fetchFollowingHighlights() {
    this.setLoading(true);
    this.setError(null);

    try {
      const highlights = await this.rootStore.authenticatedApiCall('api/highlights/following');
      
      runInAction(() => {
        this.followingHighlights = highlights;
        this.loading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error.message;
        this.loading = false;
      });
      throw error;
    }
  }

  // Create a new highlight
  async createHighlight(highlightData) {
    this.setLoading(true);
    this.setError(null);

    try {
      const formData = new FormData();
      
      // Add file if present
      if (highlightData.file) {
        formData.append('media', highlightData.file);
      }
      
      // Add other data
      Object.keys(highlightData).forEach(key => {
        if (key !== 'file') {
          formData.append(key, highlightData[key]);
        }
      });

      const newHighlight = await this.rootStore.authenticatedApiCall(
        'api/highlights',
        {},
        'POST',
        formData,
        true // file upload
      );
      
      runInAction(() => {
        this.highlights.unshift(newHighlight);
        this.loading = false;
      });

      return { success: true, highlight: newHighlight };
    } catch (error) {
      runInAction(() => {
        this.error = error.message;
        this.loading = false;
      });
      return { success: false, error: this.error };
    }
  }

  // Update a highlight
  async updateHighlight(highlightId, highlightData) {
    this.setLoading(true);
    this.setError(null);

    try {
      const updatedHighlight = await this.rootStore.authenticatedApiCall(
        `api/highlights/${highlightId}`,
        {},
        'PUT',
        highlightData
      );

      runInAction(() => {
        const index = this.highlights.findIndex(highlight => highlight.id === highlightId);
        if (index !== -1) {
          this.highlights[index] = updatedHighlight;
        }
        this.loading = false;
      });

      return { success: true, highlight: updatedHighlight };
    } catch (error) {
      runInAction(() => {
        this.error = error.message;
        this.loading = false;
      });
      return { success: false, error: this.error };
    }
  }

  // Delete a highlight
  async deleteHighlight(highlightId) {
    this.setLoading(true);
    this.setError(null);

    try {
      await this.rootStore.authenticatedApiCall(
        `api/highlights/${highlightId}`,
        {},
        'DELETE'
      );

      runInAction(() => {
        this.highlights = this.highlights.filter(highlight => highlight.id !== highlightId);
        this.loading = false;
      });

      return { success: true };
    } catch (error) {
      runInAction(() => {
        this.error = error.message;
        this.loading = false;
      });
      return { success: false, error: this.error };
    }
  }

  // Like a highlight
  async likeHighlight(highlightId) {
    this.setError(null);

    try {
      await this.rootStore.authenticatedApiCall(
        `api/likes`,
        {},
        'POST',
        {
          target_type: 'highlight',
          target_id: highlightId
        }
      );

      runInAction(() => {
        const highlight = this.highlights.find(h => h.id === highlightId);
        if (highlight) {
          highlight.userLiked = true;
          highlight.likesCount = (highlight.likesCount || 0) + 1;
        }
      });

      return { success: true };
    } catch (error) {
      runInAction(() => {
        this.error = error.message;
      });
      return { success: false, error: this.error };
    }
  }

  // Unlike a highlight
  async unlikeHighlight(highlightId) {
    this.setError(null);

    try {
      await this.rootStore.authenticatedApiCall(
        `api/likes`,
        {},
        'DELETE',
        {
          target_type: 'highlight',
          target_id: highlightId
        }
      );

      runInAction(() => {
        const highlight = this.highlights.find(h => h.id === highlightId);
        if (highlight) {
          highlight.userLiked = false;
          highlight.likesCount = Math.max(0, (highlight.likesCount || 1) - 1);
        }
      });

      return { success: true };
    } catch (error) {
      runInAction(() => {
        this.error = error.message;
      });
      return { success: false, error: this.error };
    }
  }

  // Get recent highlights
  getRecentHighlights(limit = 10) {
    return this.highlights.slice(0, limit);
  }

  // Get highlights by type
  getHighlightsByType(type) {
    return this.highlights.filter(highlight => highlight.type === type);
  }

  // Fetch a single highlight by ID
  async fetchHighlightById(highlightId) {
    this.setLoading(true);
    this.setError(null);

    try {
      let highlight;
      try {
        // Try authenticated endpoint first
        highlight = await this.rootStore.authenticatedApiCall(`api/highlights/${highlightId}`);
      } catch (authError) {
        // Fall back to public endpoint
        highlight = await this.rootStore.authenticatedApiCall(
          `api/highlights/public/${highlightId}`,
          {},
          'GET',
          null,
          false,
          false,
          true // nonAuthenticatedCall
        );
      }
      
      runInAction(() => {
        this.loading = false;
      });
      
      return highlight;
    } catch (error) {
      runInAction(() => {
        this.error = error.message;
        this.loading = false;
      });
      throw error;
    }
  }

  // Clear error
  clearError() {
    this.error = null;
  }

  // Reset store
  reset() {
    this.highlights = [];
    this.loading = false;
    this.error = null;
  }
}

export { HighlightsStore }; 